import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getColor } from "../lib/categoryColors";
import { cn } from "../lib/utils";

/**
 * categories: [{ id, name, color, sort_order }] — sorted by caller
 * activeCategory: "All" | name
 * counts: { All: n, [name]: n }
 */
export function CategoryFilter({ categories, activeCategory, onChange, counts }) {
  const buttons = [{ id: "all", name: "All", color: "slate" }, ...categories];
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 4);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState, categories.length]);

  // Translate vertical mouse-wheel input into horizontal scroll for desktop users
  const handleWheel = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // already horizontal
    if (el.scrollWidth <= el.clientWidth) return;
    el.scrollBy({ left: e.deltaY, behavior: "auto" });
    e.preventDefault();
  };

  const scrollByAmount = (delta) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  return (
    <div className="relative" data-testid="category-filter">
      {canLeft && (
        <button
          type="button"
          onClick={() => scrollByAmount(-220)}
          aria-label="Scroll categories left"
          data-testid="category-scroll-left"
          className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full bg-white border border-slate-200 shadow-md text-slate-600 hover:text-slate-900 hover:scale-105 transition-all"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          onClick={() => scrollByAmount(220)}
          aria-label="Scroll categories right"
          data-testid="category-scroll-right"
          className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full bg-white border border-slate-200 shadow-md text-slate-600 hover:text-slate-900 hover:scale-105 transition-all"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}

      <div
        ref={scrollerRef}
        onWheel={handleWheel}
        className={cn(
          "overflow-x-auto no-scrollbar -mx-4 sm:mx-0 px-4 sm:px-12 scroll-smooth",
          // edge fade only when arrows aren't shown (mobile)
          "scroll-fade-x sm:[mask-image:none] sm:[-webkit-mask-image:none]"
        )}
      >
        <div className="flex flex-nowrap items-center gap-2 min-w-max py-1">
          {buttons.map((cat) => {
            const active = activeCategory === cat.name;
            const c = getColor(cat.color);
            const testid = `filter-${cat.name.toLowerCase().replace(/\s+/g, "-")}`;
            return (
              <button
                key={cat.id}
                onClick={() => onChange(cat.name)}
                data-testid={testid}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap shrink-0",
                  "border transition-all duration-200 ease-out",
                  "hover:scale-[1.04] active:scale-100 motion-reduce:hover:scale-100",
                  active
                    ? "bg-slate-900 text-white border-slate-900 shadow-md shadow-slate-900/15"
                    : "bg-white text-slate-700 border-slate-200/80 shadow-sm hover:border-slate-300 hover:shadow-md"
                )}
              >
                <span
                  className={cn("h-2 w-2 rounded-full", c.dot)}
                  style={c.dotStyle}
                />
                <span>{cat.name}</span>
                {typeof counts?.[cat.name] === "number" && (
                  <span
                    className={cn(
                      "text-xs font-bold rounded-full px-2 py-0.5 leading-none",
                      active ? "bg-white/15 text-white" : "bg-slate-100 text-slate-500"
                    )}
                  >
                    {counts[cat.name]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
